'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

import { Eye, EyeOff, Check, X } from 'lucide-react'
import { Input } from '@luckagents/ui/primitives/text-field'

type PasswordInputProps = React.ComponentProps<'input'> & {
  strengthMeter?: boolean
}

const STRENGTH_LABELS = ['Muy débil', 'Débil', 'Aceptable', 'Fuerte', 'Muy fuerte']

const STRENGTH_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-amber-400',
  'bg-emerald-400',
  'bg-emerald-500',
]

const STRENGTH_TEXT_COLORS = [
  'text-red-500',
  'text-orange-500',
  'text-amber-500',
  'text-emerald-500',
  'text-emerald-600',
]

const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 8, label: '8+ caracteres' },
  { test: (v: string) => /[A-Z]/.test(v), label: 'Una mayúscula' },
  { test: (v: string) => /[a-z]/.test(v), label: 'Una minúscula' },
  { test: (v: string) => /\d/.test(v), label: 'Un número' },
  { test: (v: string) => /[^A-Za-z0-9]/.test(v), label: 'Un carácter especial' },
]

function getStrengthScore(value: string) {
  if (!value) return 0
  const matches = PASSWORD_RULES.reduce((score, rule) => (rule.test(value) ? score + 1 : score), 0)
  const lengthBonus = value.length >= 16 ? 1 : value.length >= 12 ? 0.5 : 0
  const normalized = Math.min(matches + lengthBonus, PASSWORD_RULES.length + 1)
  return Math.min(4, Math.round(normalized))
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ strengthMeter = false, className, ...props }, ref) {
    const { value, onChange, onFocus, onBlur, ...rest } = props
    const [visible, setVisible] = React.useState(false)
    const [focused, setFocused] = React.useState(false)
    const [internalValue, setInternalValue] = React.useState('')
    const passwordValue = typeof value === 'string' ? value : internalValue
    const score = strengthMeter ? getStrengthScore(passwordValue) : 0
    const showRules = strengthMeter && focused && passwordValue.length > 0

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInternalValue(e.target.value)
        onChange?.(e)
      },
      [onChange]
    )

    return (
      <div className="space-y-2">
        <div className="relative">
          <Input
            ref={ref}
            type={visible ? 'text' : 'password'}
            className={cn('pr-10', className)}
            value={value}
            onChange={handleChange}
            onFocus={(e) => {
              setFocused(true)
              onFocus?.(e)
            }}
            onBlur={(e) => {
              setFocused(false)
              onBlur?.(e)
            }}
            {...rest}
          />
          <button
            type="button"
            onClick={() => setVisible((current) => !current)}
            className="text-muted-foreground/70 hover:text-foreground focus-visible:ring-primary/30 focus-visible:ring-offset-background absolute right-3 top-1/2 -translate-y-1/2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            aria-pressed={visible}
          >
            {visible ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
            <span className="sr-only">{visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}</span>
          </button>
        </div>
        {strengthMeter ? (
          <StrengthMeter score={score} value={passwordValue} showRules={showRules} />
        ) : null}
      </div>
    )
  }
)

type StrengthMeterProps = {
  score: number
  value: string
  showRules: boolean
}

function StrengthMeter({ score, value, showRules }: StrengthMeterProps) {
  const label = value
    ? STRENGTH_LABELS[Math.max(0, Math.min(score, STRENGTH_LABELS.length - 1))]
    : null
  const textColor = value
    ? STRENGTH_TEXT_COLORS[Math.max(0, Math.min(score, STRENGTH_TEXT_COLORS.length - 1))]
    : 'text-muted-foreground'

  return (
    <div className="space-y-2">
      {/* Bars */}
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, index) => {
            const active = value && index < score
            const colorClass = active
              ? STRENGTH_COLORS[Math.max(0, Math.min(score, STRENGTH_COLORS.length - 1))]
              : 'bg-zinc-200 dark:bg-zinc-800'
            return (
              <span
                key={index}
                className={cn('h-1 flex-1 rounded-full transition-all duration-300', colorClass)}
              />
            )
          })}
        </div>
        {label && (
          <span
            className={cn('text-[11px] font-medium tabular-nums transition-colors', textColor)}
            aria-live="polite"
          >
            {label}
          </span>
        )}
      </div>

      {/* Rule checkmarks */}
      <div
        className={cn(
          'grid gap-0.5 overflow-hidden transition-all duration-300',
          showRules ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(value)
              return (
                <span
                  key={rule.label}
                  className={cn(
                    'flex items-center gap-1 text-[11px] transition-colors duration-200',
                    met
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-zinc-400 dark:text-zinc-500'
                  )}
                >
                  {met ? (
                    <Check className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <X className="h-3 w-3" aria-hidden="true" />
                  )}
                  {rule.label}
                </span>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
